import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SiteEvent, SiteEventList } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { localToUtcIso, utcToLocal } from '../../lib/zoneTime';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Badge, Button, Card, CheckboxField, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { SiteSelect, useActiveGroups, useFullSites } from './api';

const PAGE_SIZE = 20;
export const eventsKey = ['events-mgmt'] as const;
type When = 'upcoming' | 'past' | 'all';
const WHENS: When[] = ['upcoming', 'past', 'all'];
export const EVENT_TONE: Record<SiteEvent['status'], 'good' | 'warn' | 'bad'> = { draft: 'warn', published: 'good', cancelled: 'bad' };

/** Create or edit an event. Times are typed in the site's time zone and sent as UTC. */
export function EventForm({ event, defaultSiteId, onDone }: { event: SiteEvent | null; defaultSiteId: string; onDone: (saved: SiteEvent | null) => void }) {
  const qc = useQueryClient();
  const sites = useFullSites();
  const groups = useActiveGroups();
  const [siteId, setSiteId] = useState(event?.siteId ?? defaultSiteId);
  const zone = sites.data?.items.find((s) => s.id === siteId)?.timeZone ?? 'Europe/London';
  const toInput = (iso: string | undefined): string => {
    if (!iso) return '';
    const l = utcToLocal(iso, zone);
    return `${l.date}T${l.time}`;
  };
  const [name, setName] = useState(event?.name ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [starts, setStarts] = useState(toInput(event?.startsAt));
  const [ends, setEnds] = useState(toInput(event?.endsAt));
  const [groupIds, setGroupIds] = useState<string[]>(event?.groupIds ?? []);
  const [capacity, setCapacity] = useState(event?.capacity ? String(event.capacity) : '');
  const toIso = (v: string): string => {
    const [d, tm] = v.split('T');
    return localToUtcIso(d ?? '', tm ?? '00:00', zone);
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description,
        startsAt: toIso(starts),
        endsAt: toIso(ends),
        groupIds,
        capacity: capacity.trim() === '' ? null : Math.max(1, Math.floor(Number(capacity) || 1)),
      };
      return event
        ? api.patch(`/events-mgmt/events/${event.id}`, { body, schema: SiteEvent })
        : api.post('/events-mgmt/events', { body: { ...body, siteId }, schema: SiteEvent });
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: eventsKey });
      onDone(saved);
    },
  });
  const errs = fieldErrors(save.error);
  const toggle = (id: string) => setGroupIds((cur) => (cur.includes(id) ? cur.filter((g) => g !== id) : [...cur, id]));
  const endsBefore = starts !== '' && ends !== '' && ends <= starts;
  return (
    <Card title={event ? t('siteF.events.edit') : t('siteF.events.new')} className="mb-6">
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="sm:col-span-2">
          <ErrorLine error={save.error} />
        </div>
        <TextField label={t('siteF.events.name')} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} />
        {event ? null : <SiteSelect value={siteId} onChange={setSiteId} />}
        <TextField label={t('siteF.events.startsAt')} hint={t('siteF.events.zoneHint', { zone })} type="datetime-local" required value={starts} onChange={(e) => setStarts(e.target.value)} error={errs['startsAt']} />
        <TextField
          label={t('siteF.events.endsAt')}
          type="datetime-local"
          required
          value={ends}
          onChange={(e) => setEnds(e.target.value)}
          error={endsBefore ? t('error.event_ends_before_start') : errs['endsAt']}
        />
        <TextField label={t('siteF.events.descriptionField')} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} className="sm:col-span-2" />
        <TextField label={t('siteF.events.capacity')} hint={t('siteF.events.capacityHint')} type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
        <fieldset className="sm:col-span-2">
          <legend className="mb-1 text-sm font-medium text-slate-800">{t('siteF.events.groups')}</legend>
          <p className="mb-2 text-xs text-slate-600">{t('siteF.events.groupsHint')}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {(groups.data ?? []).map((g) => (
              <CheckboxField key={g.id} label={g.name} checked={groupIds.includes(g.id)} onChange={() => toggle(g.id)} />
            ))}
          </div>
        </fieldset>
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={save.isPending || name.trim() === '' || starts === '' || ends === '' || endsBefore || groupIds.length === 0 || siteId === ''}>
            {t('common.save')}
          </Button>
          <Button onClick={() => onDone(null)}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

/** Portal page "Events" (events-mgmt.md section 4): upcoming and past events, with their attendance. */
export function EventsPage() {
  const canManage = useCan('events.manage');
  const list = useListState();
  const [siteId, setSiteId] = useState('');
  const [when, setWhen] = useState<When>('upcoming');
  const [creating, setCreating] = useState(false);
  const sites = useFullSites();
  const zoneOf = (id: string): string => sites.data?.items.find((s) => s.id === id)?.timeZone ?? 'Europe/London';
  const siteName = (id: string): string => sites.data?.items.find((s) => s.id === id)?.name ?? '';
  const query = { siteId, when, search: list.search, page: list.page, pageSize: PAGE_SIZE };
  const q = useQuery({
    queryKey: [...eventsKey, 'list', query],
    queryFn: () => api.get('/events-mgmt/events', { query, schema: SiteEventList }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <PageHeader
        title={t('siteF.events.title')}
        description={t('siteF.events.description')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={creating} onClick={() => setCreating(true)}>
              {t('siteF.events.add')}
            </Button>
          ) : null
        }
      />
      {creating ? <EventForm event={null} defaultSiteId={siteId} onDone={() => setCreating(false)} /> : null}
      <FilterBar columns="sm:grid-cols-3">
        <SiteSelect
          allowAll
          value={siteId}
          onChange={(id) => {
            setSiteId(id);
            list.resetPage();
          }}
        />
        <SelectField
          label={t('siteF.events.when')}
          value={when}
          onChange={(e) => {
            setWhen(e.target.value as When);
            list.resetPage();
          }}
        >
          {WHENS.map((w) => (
            <option key={w} value={w}>
              {t(`siteF.events.when.${w}`)}
            </option>
          ))}
        </SelectField>
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('siteF.events.title')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('siteF.events.name')}</Th>
                <Th>{t('siteF.site')}</Th>
                <Th>{t('siteF.events.startsAt')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('siteF.events.attendance')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={5} /> : null}
              {q.data.items.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <Link className="font-medium text-brand hover:underline" to={`/events/${e.id}`}>
                      {e.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{siteName(e.siteId)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(e.startsAt, zoneOf(e.siteId))}</td>
                  <td className="px-4 py-3">
                    <Badge tone={EVENT_TONE[e.status]}>{t(`siteF.events.status.${e.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{t('siteF.events.countsLine', { arrived: e.counts.arrived, expected: e.counts.expected, walkIns: e.counts.walkIns })}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
